import { lazy } from 'react';

/**
 * A wrapper for React.lazy that retries the dynamic import when it fails.
 * This helps handle ChunkLoadError which often happens due to network issues
 * or when the dev server restarts and chunks are missing.
 * 
 * @param {Function} componentImport A function that returns a dynamic import promise.
 * @returns {React.LazyExoticComponent} A lazy-loaded component with retry logic.
 */
export const lazyWithRetry = (componentImport) =>
  lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.localStorage.getItem('page-has-been-force-refreshed') || 'false'
    );

    try {
      const component = await componentImport();
      window.localStorage.setItem('page-has-been-force-refreshed', 'false');
      return component;
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        // Log the error for debugging
        console.error('Error loading chunk, attempting force refresh:', error);
        
        // Mark that we've refreshed once to avoid infinite loops
        window.localStorage.setItem('page-has-been-force-refreshed', 'true');
        
        // Force a page refresh to get the latest bundle manifest
        window.location.reload();
        return;
      }

      // If we already refreshed and it still failed, throw the error
      console.error('Error loading chunk after refresh:', error);
      throw error;
    }
  });
